export const stageStyles = {
  draft:             { bg: "rgba(200,200,215,0.35)", fg: "hsl(220 8% 30%)",  bd: "rgba(160,160,180,0.4)" },
  pending_approval:  { bg: "rgba(220,200,255,0.5)",  fg: "hsl(265 55% 35%)", bd: "rgba(190,160,230,0.55)" },
  pending_audit:     { bg: "rgba(255,230,150,0.55)", fg: "hsl(35 90% 30%)",  bd: "rgba(230,180,80,0.55)" },
  approved:          { bg: "rgba(180,210,255,0.45)", fg: "hsl(215 80% 32%)", bd: "rgba(140,180,240,0.5)" },
  rejected:          { bg: "rgba(255,180,190,0.45)", fg: "hsl(0 70% 38%)",   bd: "rgba(240,140,150,0.5)" },
  paid:              { bg: "rgba(255,220,140,0.55)", fg: "hsl(35 90% 28%)",  bd: "rgba(220,170,60,0.55)" },
  verified:          { bg: "rgba(180,240,210,0.5)",  fg: "hsl(150 60% 25%)", bd: "rgba(120,200,170,0.55)" },
  cancelled:         { bg: "rgba(220,220,225,0.5)",  fg: "hsl(220 8% 40%)",  bd: "rgba(180,180,190,0.4)" },
};

export const stageLabel = {
  draft: "Draft",
  pending_approval: "Pending Approval",
  pending_audit: "Pending Audit",
  approved: "Approved",
  rejected: "Rejected",
  paid: "Paid",
  verified: "Verified",
  cancelled: "Cancelled",
};

export function StageBadge({ stage, approvalStep }) {
  const s = stageStyles[stage] || stageStyles.draft;
  // Multi-level chains: augment the label with "Lv N" so requesters know
  // exactly how deep the approval chain has gone.
  const showLevel = stage === "pending_approval" && typeof approvalStep === "number" && approvalStep >= 2;
  return (
    <span
      data-testid={`stage-badge-${stage}`}
      className="stage-pill"
      style={{ background: s.bg, color: s.fg, borderColor: s.bd }}
    >
      {stageLabel[stage] || stage}
      {showLevel && <span className="ml-1 opacity-75">· Lv {approvalStep}</span>}
    </span>
  );
}

export function fmtAmount(n, currency = "") {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const num = Number(n);
  return `${num.toLocaleString(undefined, { maximumFractionDigits: 8 })}${currency ? ` ${currency}` : ""}`;
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = r.result;
      const idx = s.indexOf(",");
      resolve({ filename: file.name, mime_type: file.type || "application/octet-stream", data_base64: s.slice(idx + 1) });
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
}

// Human-friendly countdown — "3h 42m", "15m", "expired 2h ago", "less than 1 min".
// Positive diff = still valid; negative = already expired.
export function fmtRemaining(iso) {
  if (!iso) return "";
  const target = Date.parse(iso);
  if (!Number.isFinite(target)) return "";
  const diffMs = target - Date.now();
  const abs = Math.abs(diffMs);
  const days = Math.floor(abs / 86_400_000);
  const hours = Math.floor((abs % 86_400_000) / 3_600_000);
  const mins = Math.floor((abs % 3_600_000) / 60_000);
  let txt;
  if (abs < 60_000) txt = "less than 1 min";
  else if (days > 0) txt = `${days}d ${hours}h`;
  else if (hours > 0) txt = `${hours}h ${mins}m`;
  else txt = `${mins}m`;
  return diffMs >= 0 ? txt : `expired ${txt} ago`;
}

export const ROLES = ["admin", "requester", "auditor", "payer"];

// Gold-forward chart palette
export const chartPalette = ["#D4A017", "#B8B8C5", "#F5C842", "#8B6914", "#E8B84A", "#7A7A85"];

// Format a "YYYY-MM" or a from/to pair into a friendly label:
//   "2026-02"                 → "Feb 2026"
//   "2026-02", "2026-04"      → "Feb 2026 – Apr 2026"
//   "2026-02", "2026-02"      → "Feb 2026" (same month → collapse)
export function fmtBillingPeriod(start, end) {
  const fmt = (s) => {
    if (!s || typeof s !== "string") return "";
    const [y, m] = s.split("-");
    if (!y || !m) return s;
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  };
  const s = fmt(start);
  if (!end || end === start) return s || "—";
  const e = fmt(end);
  return `${s} – ${e}`;
}

// Currency-specific bank field schema — shared by NewRequest & RequestDetail edit dialogs.
// Special case: "credit_card" is a payment MODE, not a currency, but we colocate its
// field schema here so both NewRequest and the RequestDetail edit dialog can reuse it.
export const CREDIT_CARD_FIELDS = [
  { k: "card_holder", label: "Cardholder name", required: true },
  { k: "card_brand", label: "Card brand (Visa/MC/Amex/etc.)" },
  { k: "last_4", label: "Last 4 digits", mono: true },
  { k: "expiry", label: "Expiry (MM/YY)", mono: true },
  { k: "billing_email", label: "Billing email / statement reference" },
];

export function bankFieldsFor(code) {
  switch ((code || "").toUpperCase()) {
    case "INR":
      return [
        { k: "account_name", label: "Account holder name", required: true },
        { k: "account_number", label: "Account number", required: true, mono: true },
        { k: "ifsc", label: "IFSC code", required: true, mono: true, upper: true },
        { k: "bank_name", label: "Bank name" },
        { k: "branch", label: "Branch" },
      ];
    case "USD":
      return [
        { k: "account_name", label: "Beneficiary name", required: true },
        { k: "account_number", label: "Account number / IBAN", required: true, mono: true },
        { k: "swift", label: "SWIFT / BIC", required: true, mono: true, upper: true },
        { k: "routing", label: "ACH routing number", mono: true },
        { k: "bank_name", label: "Bank name" },
        { k: "bank_address", label: "Bank address" },
      ];
    case "PKR":
      return [
        { k: "account_name", label: "Account holder name", required: true },
        { k: "iban", label: "IBAN", required: true, mono: true, upper: true },
        { k: "bank_name", label: "Bank name" },
        { k: "branch", label: "Branch" },
      ];
    case "BDT":
      return [
        { k: "account_name", label: "Account holder name", required: true },
        { k: "account_number", label: "Account number", required: true, mono: true },
        { k: "routing", label: "Routing number", mono: true },
        { k: "bank_name", label: "Bank name" },
        { k: "branch", label: "Branch" },
      ];
    case "AED":
      return [
        { k: "account_name", label: "Beneficiary name", required: true },
        { k: "iban", label: "IBAN", required: true, mono: true, upper: true },
        { k: "swift", label: "SWIFT / BIC", mono: true, upper: true },
        { k: "bank_name", label: "Bank name" },
      ];
    default:
      return [
        { k: "account_name", label: "Beneficiary name", required: true },
        { k: "account_number", label: "Account number / IBAN", required: true, mono: true },
        { k: "swift", label: "SWIFT / BIC", mono: true, upper: true },
        { k: "bank_name", label: "Bank name" },
      ];
  }
}
